import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { EquipmentItem, CartItem } from '../../types'
import { EQUIPMENT_ITEMS } from '../constants/equipment'

interface EquipmentStore {
  equipment: EquipmentItem[]
  cart: CartItem[]
  addToCart: (item: Omit<CartItem, 'id'>) => void
  removeFromCart: (id: string) => void
  updateCartItem: (id: string, updates: Partial<CartItem>) => void
  clearCart: () => void
  resetEquipment: () => void
  getCartTotal: () => number
  getEquipmentById: (id: string) => EquipmentItem | undefined
  getEquipmentByCategory: (category: string) => EquipmentItem[]
  getAvailableCategories: () => string[]
}

const useEquipmentStore = create<EquipmentStore>()(
  persist(
    (set, get) => ({
      equipment: EQUIPMENT_ITEMS,

      cart: [],

      addToCart: (item) => {
        const newItem: CartItem = {
          ...item,
          id: Date.now().toString(),
        }
        set((state) => ({
          cart: [...state.cart, newItem]
        }))
      },

      removeFromCart: (id) => {
        set((state) => ({
          cart: state.cart.filter(item => item.id !== id)
        }))
      },

      updateCartItem: (id, updates) => {
        set((state) => ({
          cart: state.cart.map(item =>
            item.id === id ? { ...item, ...updates } : item
          )
        }))
      },

      clearCart: () => {
        set({ cart: [] })
      },

      resetEquipment: () => {
        set({ equipment: EQUIPMENT_ITEMS })
      },

      getCartTotal: () => {
        const { cart } = get()
        return cart.reduce((total, item) => {
          return total + (item.price || 0) * item.quantity
        }, 0)
      },

      getEquipmentById: (id) => {
        return get().equipment.find(item => item.id === id)
      },

      getEquipmentByCategory: (category) => {
        return get().equipment.filter(item => item.category === category)
      },

      getAvailableCategories: () => {
        const { equipment } = get()
        return [...new Set(equipment.map(item => item.category))]
      },
    }),
    {
      name: 'fc-frick-equipment',
    }
  )
)

export { useEquipmentStore }
